import type { CommunicationFilters } from "@/models/communicationLogs";
import type { IFunder } from "@/models/funders";
import type { GetOfferTemplatesParams } from "@/models/offers";
import type { Ref } from "vue";

export const QUERY_KEYS = {
  FUNDERS: {
    all: ["funders"],
    single: (id: IFunder["id"]) => [...QUERY_KEYS.FUNDERS.all, id],
  },
  OFFER_TEMPLATES: {
    all: ["offer-templates"],
    list: (params: GetOfferTemplatesParams) =>
      [...QUERY_KEYS.OFFER_TEMPLATES.all, params] as const,
  },
  BORROWER_PLATFORM_TEMPLATES: {
    all: ["borrower-platform-templates"] as const,
    lists: () =>
      [...QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.all, "list"] as const,
    list: (filters?: unknown) =>
      [...QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.lists(), filters] as const,
    details: () =>
      [...QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.all, "detail"] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.BORROWER_PLATFORM_TEMPLATES.details(), id] as const,
  },
  PERMISSIONS_PRESETS: {
    all: ["permissions-presets"] as const,
    lists: () => [...QUERY_KEYS.PERMISSIONS_PRESETS.all, "list"] as const,
    list: (filters?: unknown) =>
      [...QUERY_KEYS.PERMISSIONS_PRESETS.lists(), filters] as const,
    details: () => [...QUERY_KEYS.PERMISSIONS_PRESETS.all, "detail"] as const,
    detail: (id: string) =>
      [...QUERY_KEYS.PERMISSIONS_PRESETS.details(), id] as const,
  },
  UNDERWRITING: {
    all: ["underwriting"] as const,
    logs: (applicationId: string) =>
      [...QUERY_KEYS.UNDERWRITING.all, "logs", applicationId] as const,
  },
  USERS: {
    all: ["users"] as const,
    byRole: (role: string, ignoreAssignedDeals?: boolean, clientId?: string) =>
      [
        ...QUERY_KEYS.USERS.all,
        "by-role",
        role,
        { ignoreAssignedDeals, clientId },
      ] as const,
    analysts: (ignoreAssignedDeals?: boolean) =>
      [...QUERY_KEYS.USERS.byRole("analyst", ignoreAssignedDeals)] as const,
    fundingAdvisors: (ignoreAssignedDeals?: boolean) =>
      [
        ...QUERY_KEYS.USERS.byRole("funding-advisor", ignoreAssignedDeals),
      ] as const,
    underwriters: (ignoreAssignedDeals?: boolean) =>
      [...QUERY_KEYS.USERS.byRole("underwriter", ignoreAssignedDeals)] as const,
    clientAnalysts: (ignoreAssignedDeals?: boolean, clientId?: string) =>
      [
        ...QUERY_KEYS.USERS.byRole(
          "client-analyst",
          ignoreAssignedDeals,
          clientId
        ),
      ] as const,
    clientFundingAdvisors: (ignoreAssignedDeals?: boolean, clientId?: string) =>
      [
        ...QUERY_KEYS.USERS.byRole(
          "client-funding-advisor",
          ignoreAssignedDeals,
          clientId
        ),
      ] as const,
    clientUnderwriters: (ignoreAssignedDeals?: boolean, clientId?: string) =>
      [
        ...QUERY_KEYS.USERS.byRole(
          "client-underwriter",
          ignoreAssignedDeals,
          clientId
        ),
      ] as const,
  },
  INBOX: {
    communicationLogs: (params?: Ref<Partial<CommunicationFilters>>) => [
      "communication-logs",
      params,
    ],
  },
} as const;
